import { useCallback, useEffect, useRef } from 'react';
import { usePoseWorkerStore } from '../../../zustand/pose-worker-store';

const OPEN = ['start', 'pause'];
const known = (order, exerciseId) => order !== null && order !== undefined && Boolean(exerciseId);

// The backend's record of the set this message closes (its reps for the pod's video of it).
const closes = (record, last) =>
  Boolean(record) &&
  record.order === last.order &&
  record.exercise_id === last.exercise_id &&
  record.set === last.set;

/**
 * The `record` message the page's state calls for, or null: `start` while the patient performs, `pause` for a
 * rest, the countdown or a modal, `stop` once the exercise ends. Pause and stop name the exercise and set that
 * started; the one after the set ends carries its `setRecord` as `result`. No start for set `held`: the set a
 * restart reset, still on the page until the backend's next frame.
 */
export function nextRecordMessage({
  recording,
  paused,
  order,
  exerciseId,
  exerciseName,
  set,
  setRecord,
  held,
  last,
}) {
  if (
    recording &&
    !paused &&
    known(order, exerciseId) &&
    !(typeof held === 'number' && held === set)
  ) {
    return {
      state: 'start',
      order,
      exercise_id: exerciseId,
      exercise_name: exerciseName || '',
      ...(typeof set === 'number' && set > 0 ? { set } : {}),
    };
  }
  if (last && OPEN.includes(last.state)) {
    const message = { ...last, state: recording ? 'pause' : 'stop' };
    if (closes(setRecord, last)) {
      message.result = setRecord;
    }
    return message;
  }
  return null;
}

/**
 * The pod's video of each set, through the LiveKit runtime; `discard()` for a restart or a mode switch (every set).
 * `replayed` changes when a reconnect replays set `replayedSet` (Django's `setReplayed`): that set's take goes.
 */
export function useExerciseVideoControl({
  recording,
  paused,
  order,
  exerciseId,
  exerciseName,
  set,
  setRecord,
  replayed,
  replayedSet,
}) {
  const runtime = usePoseWorkerStore(state => state.runtime);
  const lastRef = useRef(null);
  const replayedRef = useRef(null);
  const heldRef = useRef(null);

  const send = useCallback(
    message => {
      lastRef.current = message;
      runtime?.setRecordState?.(message);
    },
    [runtime]
  );

  // Without a set, every set of the exercise.
  const discard = useCallback(
    discardSet => {
      if (known(order, exerciseId)) {
        if (typeof discardSet !== 'number') {
          heldRef.current = set ?? null;
        }
        send({
          state: 'discard',
          order,
          exercise_id: exerciseId,
          exercise_name: exerciseName || '',
          ...(typeof discardSet === 'number' ? { set: discardSet } : {}),
        });
      }
    },
    [send, order, exerciseId, exerciseName, set]
  );

  useEffect(() => {
    if (
      runtime?.setRecordState &&
      replayed &&
      replayed !== replayedRef.current &&
      known(order, exerciseId)
    ) {
      replayedRef.current = replayed;
      discard(replayedSet);
    }
  }, [runtime, replayed, replayedSet, order, exerciseId, discard]);

  useEffect(() => {
    if (!runtime?.setRecordState) {
      return;
    }
    if (heldRef.current !== null && set !== heldRef.current) {
      heldRef.current = null;
    }
    const last = lastRef.current;
    const message = nextRecordMessage({
      recording,
      paused,
      order,
      exerciseId,
      exerciseName,
      set,
      setRecord,
      held: heldRef.current,
      last,
    });
    if (message) {
      send(message);
    }
  }, [runtime, recording, paused, order, exerciseId, exerciseName, set, setRecord, send]);

  return { discard: () => discard() };
}
